import { useCallback, useEffect, useState } from 'react'
import axios from 'axios'
import { useAuth } from '../../../../modules/auth'
import { STORAGE_NAMES } from '../../../../tfi/helpers/consts'
import { URL } from '../../../gql/client'
export type ChannelConfigScope = 'organization' | 'group' | 'user'
export type TelephonyProviderValue = '' | 'RINGCENTRAL' | 'VICIDIAL'
export type SaveConfigResult = { ok: true } | { ok: false; error: string }

export interface VicidialCampaignOption {
  campaign_id: string
  campaign_name: string
  active: string
}

export interface ChannelConfigData {
  id: number | null
  webhook_token: string
  organization_id?: string
  organization_name?: string
  user_id?: string
  user_name?: string
  telephony_provider: TelephonyProviderValue
  vicidial_enabled: boolean
  vicidial_base_url: string
  vicidial_api_user: string
  vicidial_api_password: string
  vicidial_agent_user: string
  vicidial_agent_password: string
  vicidial_phone_login: string
  vicidial_phone_password: string
  vicidial_phone_code: string
  vicidial_campaign_id: string
  vicidial_dial_queue_delay_seconds: number | null
  vicidial_webphone_url: string
  vicidial_supervisor_access: boolean | null
  twilio_provisioning_enabled: boolean
  twilio_provisioning_max_numbers: number
  // General Whatsapp Provider
  whatsapp_provider: 'NONE' | 'BOTMAKER' | 'TWILIO' | 'META'

  // Twilio
  twilio_enabled: boolean
  twilio_account_sid: string
  twilio_auth_token: string
  twilio_phone_number: string
  twilio_elastic_trunk_sid: string
  twilio_whatsapp_number: string
  twilio_whatsapp_account_sid: string
  twilio_whatsapp_auth_token: string

  // Botmaker
  botmaker_enabled: boolean
  botmaker_api_url: string
  botmaker_api_token: string
  botmaker_default_channel_id: string
  botmaker_bot_name: string
  botmaker_phone_line_number: string

  // Meta Cloud API
  meta_whatsapp_access_token: string
  meta_whatsapp_phone_number_id: string
  meta_whatsapp_verify_token: string
  meta_whatsapp_phone_display: string

  // Email
  email_enabled: boolean
  email_host: string
  email_port: number
  email_host_user: string
  email_host_password: string
  email_use_tls: boolean
  email_from: string
  email_reply_to: string

  // HealthSherpa Medicare
  healthsherpa_enabled: boolean
  healthsherpa_email: string
  healthsherpa_api_key: string
  healthsherpa_webhook_secret: string

  // Dify AI
  dify_enabled: boolean
  dify_api_key: string
  dify_api_url: string
  dify_app_id: string

  // IA Conversacional: selector de proveedor
  ai_provider: 'DIFY' | 'LLM'
  llm_provider: 'ANTHROPIC' | 'OPENAI' | ''
  llm_api_key: string
  llm_model: string

  // Prompt de IA Conversacional + datos referenciales
  prompt_header: string
  prompt_body: string
  prompt_footer: string
  share_contact_info: boolean | null
  share_quotes: boolean | null
  share_policies: boolean | null
  share_leads: boolean | null
  share_consent_status: boolean | null

  // EZLynx
  ezlynx_enabled: boolean
  ezlynx_client_id: string
  ezlynx_client_secret: string
  ezlynx_username: string
  ezlynx_integration_group_id: string
  ezlynx_base_url: string
  ezlynx_auth_url: string
}

const EMPTY_CONFIG: ChannelConfigData = {
  id: null,
  webhook_token: '',
  telephony_provider: 'RINGCENTRAL',
  vicidial_enabled: false,
  vicidial_base_url: '',
  vicidial_api_user: '',
  vicidial_api_password: '',
  vicidial_agent_user: '',
  vicidial_agent_password: '',
  vicidial_phone_login: '',
  vicidial_phone_password: '',
  vicidial_phone_code: '1',
  vicidial_campaign_id: '',
  vicidial_dial_queue_delay_seconds: 5,
  vicidial_webphone_url: '',
  vicidial_supervisor_access: null,
  twilio_provisioning_enabled: false,
  twilio_provisioning_max_numbers: 2,
  whatsapp_provider: 'NONE',

  twilio_enabled: false, twilio_account_sid: '', twilio_auth_token: '', twilio_phone_number: '', twilio_elastic_trunk_sid: '', twilio_whatsapp_number: '', twilio_whatsapp_account_sid: '', twilio_whatsapp_auth_token: '',

  botmaker_enabled: false, botmaker_api_url: 'https://api.botmaker.com/v2.0', botmaker_api_token: '',
  botmaker_default_channel_id: '', botmaker_bot_name: '', botmaker_phone_line_number: '',

  meta_whatsapp_access_token: '', meta_whatsapp_phone_number_id: '', meta_whatsapp_verify_token: '', meta_whatsapp_phone_display: '',

  email_enabled: false, email_host: '', email_port: 587, email_host_user: '',
  email_host_password: '', email_use_tls: true, email_from: '', email_reply_to: '',

  healthsherpa_enabled: false, healthsherpa_email: '', healthsherpa_api_key: '', healthsherpa_webhook_secret: '',

  dify_enabled: false, dify_api_key: '', dify_api_url: 'https://api.dify.ai/v1', dify_app_id: '',
  ai_provider: 'DIFY', llm_provider: 'ANTHROPIC', llm_api_key: '', llm_model: '',
  prompt_header: '', prompt_body: '', prompt_footer: '',
  share_contact_info: null, share_quotes: null, share_policies: null, share_leads: null, share_consent_status: null,
  ezlynx_enabled: false,
  ezlynx_client_id: '',
  ezlynx_client_secret: '',
  ezlynx_username: '',
  ezlynx_integration_group_id: '',
  ezlynx_base_url: 'https://api.ezlynx.com',
  ezlynx_auth_url: 'https://api.ezlynx.com/auth/connect/token',
}

const getScopeParam = (scope: ChannelConfigScope) => {
  if (scope === 'organization') return 'organization_id'
  if (scope === 'group') return 'group_id'
  return 'user_id'
}

const normalizeConfig = (
  data: Partial<ChannelConfigData> | undefined,
  scope: ChannelConfigScope
): ChannelConfigData => ({
  ...EMPTY_CONFIG,
  ...data,
  telephony_provider:
    scope === 'user'
      ? ((data?.telephony_provider as TelephonyProviderValue) || '')
      : ((data?.telephony_provider as TelephonyProviderValue) || 'RINGCENTRAL'),
  vicidial_phone_code: data?.vicidial_phone_code || '1',
  vicidial_dial_queue_delay_seconds:
    data?.vicidial_dial_queue_delay_seconds === null || data?.vicidial_dial_queue_delay_seconds === undefined
      ? scope === 'user'
        ? null
        : 5
      : Number(data.vicidial_dial_queue_delay_seconds),
  webhook_token: data?.webhook_token || '',
})

export const fetchScopedChannelConfig = async ({
  scope,
  targetId,
  token,
}: {
  scope: ChannelConfigScope
  targetId?: string
  token: string
}) => {
  const query = targetId ? `?${getScopeParam(scope)}=${targetId}` : ''
  const res = await axios.get(`${URL}/api/chat/channel-config/${scope}${query}`, {
    headers: { Authorization: `Bearer ${token}` },
  })
  return normalizeConfig(res.data, scope)
}

export const saveScopedChannelConfig = async ({
  scope,
  targetId,
  token,
  data,
}: {
  scope: ChannelConfigScope
  targetId?: string
  token: string
  data: Partial<ChannelConfigData>
}) => {
  const payload: Record<string, any> = { ...data }
  if (targetId) {
    payload[getScopeParam(scope)] = targetId
  }

  const res = await axios.post(`${URL}/api/chat/channel-config/${scope}`, payload, {
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
  })
  return normalizeConfig(res.data, scope)
}

export const fetchVicidialCampaignOptions = async ({
  token,
  baseUrl,
  apiUser,
  apiPassword,
}: {
  token: string
  baseUrl?: string
  apiUser?: string
  apiPassword?: string
}) => {
  const response = await axios.post(
    `${URL}/api/telephony/vicidial/campaigns`,
    {
      ...(baseUrl ? { vicidial_base_url: baseUrl } : {}),
      ...(apiUser ? { vicidial_api_user: apiUser } : {}),
      ...(apiPassword ? { vicidial_api_password: apiPassword } : {}),
    },
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }
  )

  return (response.data?.campaigns || []) as VicidialCampaignOption[]
}

const useScopedChannelConfig = (scope: ChannelConfigScope, targetId?: string) => {
  const { auth } = useAuth()
  const [config, setConfig] = useState<ChannelConfigData>(normalizeConfig(undefined, scope))
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [testing, setTesting] = useState(false)

  const getToken = useCallback(() => {
    return auth?.api_token || localStorage.getItem(STORAGE_NAMES.TOKEN)
  }, [auth?.api_token])

  const testConnection = useCallback(
    async (data: Partial<ChannelConfigData>) => {
      const token = getToken()
      if (!token) return { success: false, message: 'No token found' }

      setTesting(true)
      try {
        const res = await axios.post(`${URL}/api/chat/channel-config/ezlynx/test`, data, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        })
        return res.data
      } catch (error: any) {
        return {
          success: false,
          message: error.response?.data?.message || 'Error testing connection',
        }
      } finally {
        setTesting(false)
      }
    },
    [getToken]
  )

  const fetchConfig = useCallback(async () => {
    const token = getToken()
    if (!token || ((scope === 'user' || scope === 'group') && !targetId)) {
      setConfig(normalizeConfig(undefined, scope))
      return
    }

    setLoading(true)
    try {
      const nextConfig = await fetchScopedChannelConfig({ scope, targetId, token })
      setConfig(nextConfig)
    } catch (error) {
      console.error(`Error fetching ${scope} channel config:`, error)
    } finally {
      setLoading(false)
    }
  }, [getToken, scope, targetId])

  const saveConfig = useCallback(
    async (data: Partial<ChannelConfigData>): Promise<SaveConfigResult> => {
      const token = getToken()
      if (!token || ((scope === 'user' || scope === 'group') && !targetId)) {
        return { ok: false, error: 'Missing authentication or target' }
      }

      setSaving(true)
      try {
        const nextConfig = await saveScopedChannelConfig({ scope, targetId, token, data })
        setConfig(nextConfig)
        return { ok: true }
      } catch (error: any) {
        const detail =
          error?.response?.data?.error ||
          error?.response?.data?.detail ||
          error?.message ||
          'Unknown error'
        console.error(`Error saving ${scope} channel config:`, error)
        return { ok: false, error: detail }
      } finally {
        setSaving(false)
      }
    },
    [getToken, scope, targetId]
  )

  useEffect(() => {
    fetchConfig()
  }, [fetchConfig])

  return { config, setConfig, loading, saving, testing, fetchConfig, saveConfig, testConnection }
}

export const useChannelConfig = (organizationId?: string) => {
  const scoped = useScopedChannelConfig('organization', organizationId)
  return {
    ...scoped,
    fetchOrgConfig: scoped.fetchConfig,
    saveOrgConfig: scoped.saveConfig,
  }
}

export const useGroupChannelConfig = (groupId?: string) => {
  const scoped = useScopedChannelConfig('group', groupId)
  return {
    ...scoped,
    fetchGroupConfig: scoped.fetchConfig,
    saveGroupConfig: scoped.saveConfig,
  }
}

export const useUserChannelConfig = (userId?: string) => {
  const scoped = useScopedChannelConfig('user', userId)
  return {
    ...scoped,
    fetchUserConfig: scoped.fetchConfig,
    saveUserConfig: scoped.saveConfig,
  }
}

export const useResolvedChannelConfig = () => {
  const { auth } = useAuth()
  const [config, setConfig] = useState<ChannelConfigData>(normalizeConfig(undefined, 'organization'))
  const [loading, setLoading] = useState(false)

  const getToken = useCallback(() => {
    return auth?.api_token || localStorage.getItem(STORAGE_NAMES.TOKEN)
  }, [auth?.api_token])

  const fetchConfig = useCallback(async () => {
    const token = getToken()
    if (!token) return

    setLoading(true)
    try {
      const res = await axios.get(`${URL}/api/chat/channel-config/resolve`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      setConfig(normalizeConfig(res.data, 'organization'))
    } catch (error) {
      console.error(`Error resolving channel config:`, error)
    } finally {
      setLoading(false)
    }
  }, [getToken])

  useEffect(() => {
    fetchConfig()
  }, [fetchConfig])

  return { config, loading, fetchConfig }
}
